"use client";

import { useEffect, useMemo } from "react";
import BlogCard from "@/app/components/ui/blog-card";
import FallbackMessage from "@/app/components/ui/fallback-message";
import { usePostsFilterStore } from "@/app/stores/posts-filter-store";
import type { PostWithRelationsT } from "@/app/types/posts";
import { cn } from "@/lib/utils";
import SimplePaginationControls from "./simple-pagination-controls";

interface BlogPostsGridProps {
  posts: PostWithRelationsT[];
  className?: string;
}

export default function BlogPostsGrid({
  posts,
  className,
}: BlogPostsGridProps) {
  const {
    setPosts,
    filteredPosts,
    isInitialized,
    currentPage,
    pageSize,
    setCurrentPage,
  } = usePostsFilterStore();

  // Initialize store with posts
  useEffect(() => {
    setPosts(posts);
  }, [posts, setPosts]);

  // Calculate pagination
  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredPosts.slice(startIndex, endIndex);
  }, [filteredPosts, currentPage, pageSize]);

  const totalItems = filteredPosts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  // Show loading state until store is initialized
  if (!isInitialized) {
    return (
      <ul
        className={cn(
          "mt-8 grid grid-cols-1 gap-6 gap-y-8 md:mt-12 md:grid-cols-2 md:gap-y-12 lg:grid-cols-3",
          className
        )}
        role="list"
      >
        {posts.map((post) => (
          <li key={post.id} role="listitem">
            <BlogCard
              image={post.headerImageAsset?.url || null}
              title={post.title}
              subtitle={post.sub_title || ""}
              link={`/beitraege/${post.slug}`}
              categories={post.categories}
              altText={post.headerImageAsset?.alt_text || ""}
              consumptionText={post.consumption_text || ""}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <div id="beitraege-section-top" />
      {paginatedPosts.length > 0 ? (
        <>
          <ul
            className={cn(
              "mt-8 grid grid-cols-1 gap-6 gap-y-8 md:mt-12 md:grid-cols-2 md:gap-y-12 lg:grid-cols-3",
              className
            )}
            role="list"
          >
            {paginatedPosts.map((post) => (
              <li key={post.id} role="listitem">
                <BlogCard
                  image={post.headerImageAsset?.url || null}
                  title={post.title}
                  subtitle={post.sub_title || ""}
                  link={`/beitraege/${post.slug}`}
                  categories={post.categories}
                  altText={post.headerImageAsset?.alt_text || ""}
                  consumptionText={post.consumption_text || ""}
                />
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <>
              <div className="mt-8 flex items-center justify-between text-sm text-[#5c5c5c]">
                <span>
                  Seite {safeCurrentPage} von {totalPages}
                </span>
                <span>
                  {totalItems > 0
                    ? `${startIndex + 1}–${endIndex} von ${totalItems}`
                    : `0 von 0`}
                </span>
              </div>
              <SimplePaginationControls
                totalPages={totalPages}
                currentPage={safeCurrentPage}
                onPageChange={setCurrentPage}
                scrollToId="beitraege-section-top"
              />
            </>
          )}
        </>
      ) : (
        <FallbackMessage
          title="Keine Beiträge gefunden"
          description="Es gibt keine Beiträge, die Ihren Suchkriterien entsprechen."
        />
      )}
    </>
  );
}
